import React from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import I18n from 'modules/i18n';
import * as Routes from '@/modules/routes';
import ActionsDropdown from '@/components/common/ActionsDropdown';
import TCIOnlyLock from '@/components/common/TCIOnlyLock';
import { isSysadmin } from '@/components/common/Utils';
import showToast from '@/components/common/Toast';
import Icon from '@/components/ui/Icon';
import { TCITooltip } from '@/components/ui/Tooltip';
import ImpersonateModal from './ImpersonateModal';

const StaffDropdown = ({ currentStafferId, manageProgramsPath, rowData, openModal, transferPath, userType }) => {
  const isSelf = rowData.id === currentStafferId;

  const _renderTransfer = () => {
    if (transferPath) {
      return (
        <a
          className="dropdown-item"
          href={transferPath.concat(`&teacher[]=${rowData.id}`)}
        >
          <i aria-hidden="true" className="fa fa-exchange" /> Transfer <TCIOnlyLock />
        </a>
      );
    }

    return null;
  };

  const handlePasswordReset = () => {
    let message = 'Something went wrong. Please try again later.';
    const messageOpts = { autoClose: 5000, msgType: 'error' };

    Axios
      .post(Routes.plato_api_reset_password_staffer_path(rowData.id))
      .then((res) => {
        if (res.data.success) {
          message = `Password reset email sent to ${rowData.email}.`;
          messageOpts.msgType = 'success';
        }
        showToast(message, messageOpts);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.log(e);
        showToast(message, messageOpts);
      });
  };

  const EditButton = (
    <a
      className="dropdown-item"
      href="#"
      role="button"
      onClick={e => openModal(e, rowData, 'edit')}
    >
      <i aria-hidden="true" className="fa fa-edit" /> Edit
    </a>
  );

  return (
    <ActionsDropdown>
      {EditButton}
      {isSysadmin(userType) && (
        <a
          className="dropdown-item tw-flex tw-items-center"
          href="#"
          role="button"
          onClick={handlePasswordReset}
        >
          <Icon
            className="tw-pr-2"
            icon="ResetPassword"
            size={14}
          />
          Reset Password
        </a>
      )}
      <TCITooltip disabled={!isSelf}>
        <TCITooltip.Trigger asChild>
          <a
            aria-disabled={isSelf}
            className={`dropdown-item ${isSelf ? 'tw-opacity-50 tw-cursor-not-allowed' : ''}`.trim()}
            href="#"
            role="button"
            onClick={isSelf ? e => e.preventDefault() : e => openModal(e, rowData, 'delete')}
          >
            <i aria-hidden="true" className="fa fa-trash" /> Delete
          </a>
        </TCITooltip.Trigger>
        <TCITooltip.Content>
          {I18n.t('cannot_delete_own_account')}
        </TCITooltip.Content>
      </TCITooltip>

      <div className="dropdown-divider" />

      <a
        className="dropdown-item"
        href={manageProgramsPath.replace(':teacher_id', rowData.id)}
      >
        <i aria-hidden="true" className="fa fa-edit" /> Manage Programs
      </a>

      {_renderTransfer()}

      <ImpersonateModal staffer={rowData} userType={userType} />
    </ActionsDropdown>
  );
};

StaffDropdown.propTypes = {
  currentStafferId: PropTypes.number.isRequired,
  manageProgramsPath: PropTypes.string.isRequired,
  openModal: PropTypes.func.isRequired,
  rowData: PropTypes.shape({
    email: PropTypes.string.isRequired,
    first_name: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    last_name: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    username: PropTypes.string,
  }).isRequired,
  transferPath: PropTypes.string,
  userType: PropTypes.string.isRequired
};

StaffDropdown.defaultProps = {
  transferPath: null
};

export default StaffDropdown;
